import { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TextField,
} from "@mui/material";
import { RestartAlt, VisibilityOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_ORIGIN as API_URL } from "../../config/env";
import { getAuthHeaders } from "../../utils/auth";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import { AdminAction, AdminPageLayout, AdminSectionCard, TableRowActions } from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";
import { apiErrorMessage, formatDate, formatMoney, previewList } from "./farmDairyUtils";

const emptyFilters = {
  state: "",
  district: "",
  entryType: "all",
  range: "all",
  fromDate: "",
  toDate: "",
};

export default function FarmDairyListPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [filterKey, setFilterKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const hasActiveFilters = Boolean(
    searchInput.trim() ||
      filters.state ||
      filters.district ||
      filters.entryType !== "all" ||
      filters.range !== "all"
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;

    async function loadUsers() {
      setLoading(true);
      setError("");
      try {
        const params = {
          page: page + 1,
          limit: rowsPerPage,
        };
        if (search) params.search = search;
        if (filters.state) params.state = filters.state;
        if (filters.district) params.district = filters.district;
        if (filters.entryType !== "all") params.entryType = filters.entryType;
        if (filters.range === "last30") params.range = "last30";
        if (filters.range === "custom") {
          params.range = "custom";
          if (filters.fromDate) params.fromDate = filters.fromDate;
          if (filters.toDate) params.toDate = filters.toDate;
        }

        const res = await axios.get(`${API_URL}/api/v1/farm-diary/admin/users`, {
          headers: getAuthHeaders(),
          params,
        });
        if (cancelled) return;
        setRows(res.data?.data?.users || []);
        setTotal(Number(res.data?.data?.total) || 0);
      } catch (err) {
        if (cancelled) return;
        const message = apiErrorMessage(err, "Failed to load farm diary records");
        setRows([]);
        setTotal(0);
        setError(message);
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [page, rowsPerPage, search, filters]);

  const updateFilter = (patch) => {
    setFilters((current) => ({ ...current, ...patch }));
    setPage(0);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    setFilters(emptyFilters);
    setPage(0);
    setFilterKey((key) => key + 1);
  };

  return (
    <AdminPageLayout
      eyebrow="Farmer records"
      title="Farm Diary"
      description="Review each farmer's crop diary entries and farm activities, including categories, dates, and income or expense totals."
      actions={
        <div className="admin-toolbar" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
          <TextField
            key={`search-${filterKey}`}
            id="farm-dairy-search"
            label="Search name, mobile, crop, or category"
            value={searchInput}
            size="small"
            sx={{ minWidth: 260 }}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <TextField
            key={`state-${filterKey}`}
            id="farm-dairy-state"
            size="small"
            label="State"
            value={filters.state}
            sx={{ minWidth: 140 }}
            onChange={(event) => updateFilter({ state: event.target.value })}
          />
          <TextField
            key={`district-${filterKey}`}
            id="farm-dairy-district"
            size="small"
            label="District"
            value={filters.district}
            sx={{ minWidth: 140 }}
            onChange={(event) => updateFilter({ district: event.target.value })}
          />
          <TextField
            id="farm-dairy-entry-type"
            select
            size="small"
            label="Entry type"
            value={filters.entryType}
            sx={{ minWidth: 140 }}
            onChange={(event) => updateFilter({ entryType: event.target.value })}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="income">Income</MenuItem>
            <MenuItem value="expense">Expense</MenuItem>
          </TextField>
          <TextField
            id="farm-dairy-range"
            select
            size="small"
            label="Date range"
            value={filters.range}
            sx={{ minWidth: 150 }}
            onChange={(event) => updateFilter({ range: event.target.value })}
          >
            <MenuItem value="all">All dates</MenuItem>
            <MenuItem value="last30">Last 30 days</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </TextField>
          {filters.range === "custom" && (
            <>
              <TextField
                id="farm-dairy-from"
                size="small"
                type="date"
                label="From"
                value={filters.fromDate}
                onChange={(event) => updateFilter({ fromDate: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                id="farm-dairy-to"
                size="small"
                type="date"
                label="To"
                value={filters.toDate}
                onChange={(event) => updateFilter({ toDate: event.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}
          <AdminAction
            id="reset-filters-btn"
            variant="outline"
            startIcon={<RestartAlt />}
            disabled={!hasActiveFilters}
            onClick={handleReset}
            sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
          >
            Reset
          </AdminAction>
        </div>
      }
    >
      <AdminSectionCard
        title="Farm diary list"
        description="Amounts are each farmer's full diary totals. A date range or entry type chooses which farmers appear, and still includes their complete income and expense."
        className="admin-table-shell"
      >
        <TableContainer component="div">
          <Table sx={{ minWidth: 980 }} aria-label="farm diary table">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell>User</StyledTableCell>
                <StyledTableCell>Location</StyledTableCell>
                <StyledTableCell>Crops</StyledTableCell>
                <StyledTableCell>Activities</StyledTableCell>
                <StyledTableCell>Records</StyledTableCell>
                <StyledTableCell>Income</StyledTableCell>
                <StyledTableCell>Expense</StyledTableCell>
                <StyledTableCell>Latest date</StyledTableCell>
                <StyledTableCell align="right">Actions</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 4, color: "#78716c" }}>
                      <CircularProgress size={22} sx={{ color: "#3f7a2a" }} />
                      <span>Loading farm diary records…</span>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ) : error ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center">
                    <Box sx={{ py: 4, color: "#c5543d" }}>{error}</Box>
                  </StyledTableCell>
                </StyledTableRow>
              ) : rows.length === 0 ? (
                <StyledTableRow>
                  <StyledTableCell colSpan={9} align="center">
                    <Box sx={{ py: 4, color: "#78716c" }}>
                      {hasActiveFilters
                        ? "No farm diary records match these filters."
                        : "No farm diary records found."}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ) : (
                rows.map((user) => (
                  <StyledTableRow key={user.id}>
                    <StyledTableCell>
                      <div className="font-semibold text-[15px] text-[#172112]">
                        {user.name || "Unknown user"}
                      </div>
                      <div className="text-xs text-zinc-500">{user.mobile || "No mobile"}</div>
                      {user.isDeleted ? <Chip label="Deleted" size="small" color="warning" sx={{ mt: 0.5 }} /> : null}
                    </StyledTableCell>
                    <StyledTableCell>
                      <div>{[user.district, user.state].filter(Boolean).join(", ") || "—"}</div>
                      {user.village ? <div className="text-xs text-zinc-500">{user.village}</div> : null}
                    </StyledTableCell>
                    <StyledTableCell>
                      <div title={user.crops?.join(", ")}>{previewList(user.crops)}</div>
                      <div className="text-xs text-zinc-500" title={user.categories?.join(", ")}>
                        {user.categories?.length ? previewList(user.categories) : "No category"}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      <div title={user.activityNames?.join(", ")}>{previewList(user.activityNames)}</div>
                      <div className="text-xs text-zinc-500" title={user.activityCategories?.join(", ")}>
                        {user.activityCategories?.length ? previewList(user.activityCategories) : "No activity"}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell>
                      <div>{user.entryCount} crop {user.entryCount === 1 ? "entry" : "entries"}</div>
                      <div className="text-xs text-zinc-500">
                        {user.activityCount} {user.activityCount === 1 ? "activity" : "activities"}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell sx={{ color: "#166534" }}>{formatMoney(user.totalIncome)}</StyledTableCell>
                    <StyledTableCell sx={{ color: "#b91c1c" }}>{formatMoney(user.totalExpense)}</StyledTableCell>
                    <StyledTableCell>{formatDate(user.latestDate)}</StyledTableCell>
                    <StyledTableCell align="right">
                      <TableRowActions
                        rowId={user.id}
                        customActions={[
                          {
                            key: "details",
                            label: "View Details",
                            icon: <VisibilityOutlined fontSize="small" />,
                            onClick: () => navigate(`/farm-dairy/${user.id}`),
                          },
                        ]}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={loading ? 0 : total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </AdminSectionCard>
    </AdminPageLayout>
  );
}
